import { useCustomNavigation } from "@app/components/navigation/NavigationHandler"
import OrderContentBody from "@app/components/order/OrderContentBody/OrderContentBody"
import OrderSubPage from "@app/components/pageTypes/OrderSubPage/OrderSubPage"
import { faMinus } from "@fortawesome/free-solid-svg-icons/faMinus"
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus"
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"

import { ContentFooter } from "@app/components/core/ContentFooter/ContentFooter"
import { ContentHeader } from "@app/components/core/ContentHeader/ContentHeader"
import { Decoration } from "@app/components/map/Decoration"
import { Hotspot } from "@app/components/map/Hotspot"
import { LoungerGroup } from "@app/components/map/LoungerGroup"
import { OrderAnimationTypes } from "@app/components/routing/AnimatedOrderRoute/AnimatedOrderRoute"
import { useRaiseError } from "@app/store/actions/errorActions"
import { selectLoungerId, setObjectGroupOptions } from "@app/store/actions/orderActions"
import { useGetLoungerTypes, useGetStrings } from "@app/store/service/cmsService"
import {
	useDeleteObjectOption,
	useGetHotspots,
	useGetLoungerTags,
	useGetMapDecorations,
	useGetMaps,
	useGetObjects,
	useSaveObjectGroupOptions,
	useSaveObjectOptions,
} from "@app/store/service/connectorService"
import { appStore } from "@app/store/store"
import { handleKeyboardSelect, handleKeyPress } from "@app/utils/accessibility"
import { KEYBOARD_KEY_SET, OrderErrors } from "@app/utils/constants"
import { getPriceString } from "@app/utils/localeUtils"
import {
	selectCountOfLoungerWithSelection,
	selectFullPrice,
	selectMinimumCapacity,
	selectObjectGroups,
	selectSelectedObjectIds,
	selectUsedCapacity,
} from "@app/utils/selectors"
import tracking from "@app/utils/tracking"
import { faCheck, faUser } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { classNamez, useCallbackRef } from "@tecreation/funnel-components"
import { ResponseError, useSelector } from "@tecreation/js-state-react"
import "./LoungerMapSelection.css"

export const LoungerMapSelection = () => {
	const selectedObjectGroups = useSelector(selectObjectGroups)
	const minimumCapacity = useSelector(selectMinimumCapacity)
	const usedCapacity = useSelector(selectUsedCapacity)
	const price = useSelector(selectFullPrice)
	const selectedObjectIds = useSelector(selectSelectedObjectIds)
	const loungers = useSelector(state => state.order.loungers)
	const countOfLoungerWithSelection = useSelector(selectCountOfLoungerWithSelection)
	const selectedDay = useSelector(state => state.order.selectedDay)
	const objectGroupOptions = useSelector(state => state.order.objectGroupOptions)
	const availableLoungers = useSelector(state => state.order.availableLoungers)

	const [addedGroup, setAddedGroup] = useState(undefined)
	const [__, forceRender] = useState({})
	const [map, setMap] = useState(null)
	const [showHotspots, setShowHotspots] = useState(true)
	const [selectedTags, setSelectedTags] = useState([])
	const [isFocusInsideTheMap, setIsFocusInsideTheMap] = useState(false)
	const [currentLoungerGroupInteraction, setCurrentLoungerGroupInteraction] = useState(null)

	const svg = useRef(null)
	const zoomLevel = useRef(1)
	const eventCache = useRef([])
	const prevPinchDiff = useRef(-1)
	const pointerStart = useRef({
		x: 0,
		y: 0,
		posX: 0,
		posY: 0,
		width: 0,
		height: 0,
	})
	const initialViewBox = useRef(null)
	const allLoungersRef = useRef([])
	const lastFocusedMapElement = useRef(null)

	const { push, pop } = useCustomNavigation()
	const { handleRef: parentHandler, ref: parentRef } = useCallbackRef()
	const raiseError = useRaiseError()

	const { data: maps } = useGetMaps({ zoneId: appStore.get("core.zoneId") })
	const { data: hotspots } = useGetHotspots({ zoneId: appStore.get("core.zoneId") })
	const { data: objects } = useGetObjects({ zoneId: appStore.get("core.zoneId") })
	const { data: tags } = useGetLoungerTags({ zoneId: appStore.get("core.zoneId") })
	const { data: decorations } = useGetMapDecorations({ zoneId: appStore.get("core.zoneId") })
	const { data: __types } = useGetLoungerTypes({ locationId: appStore.getState().core.locationId, zoneId: appStore.getState().core.zoneId })
	const [saveObjectsOptions] = useSaveObjectOptions()
	const [saveObjectGroupOptions, { isFetching: __isSavingObjectGroups }] = useSaveObjectGroupOptions()
	const {
		data: {
			loungerMapSelectionTitleSingle,
			buttonNext,
			buttonAutomaticAssign,
			buttonLabelZoomIn,
			buttonLabelZoomOut,
			for: forString,
			mapControlsLabel,
			mapSvgLabel,
			mapSvgOnSelectLabel,
			zoomControlsLabel,
			addedToCartLabel,
			removedFromCartLabel,
		},
	} = useGetStrings(undefined, { defaultData: {} })
	const [deleteReserverationForId] = useDeleteObjectOption()
	const relation = appStore.get("core.relation")
	const isHandicapped = relation && relation.tags ? (relation.tags.find(e => e.tag === "Handicap") ? true : false) : false

	useLayoutEffect(() => {
		if (map) {
			document.getElementById("divMap").innerHTML = map.svg
		}
	}, [map])

	useEffect(() => {
		const f = async function () {
			//remove reserveration on mount
			Promise.all(
				objectGroupOptions.map(object => {
					return deleteReserverationForId({ objectOptionId: object.objectOptionId }).unwrap()
				}),
			).catch(e => {
				if (e instanceof ResponseError) {
					if (e && e.response.data && e.response.data.errorMessage && e.response.data.errorMessage.startsWith("wrn.Object.OptionNotFound")) {
						return
					}
				}
				raiseError(e, undefined, true)
			})
		}
		f()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (maps && hotspots && objects && !map) {
			let map = maps[0]

			for (let m of maps) {
				let mapId = m.mapId
				let selectMap = false
				for (let selectedGroupOption of selectedObjectGroups) {
					let objectGroup = objects.find(e => e.objectGroupId === selectedGroupOption)
					let founded = false
					if (objectGroup) {
						//search a option that is available inside the selectedGroup
						for (let option of objectGroup.objects.filter(o => o.mapId === mapId)) {
							const isAvailable =
								availableLoungers.find(available => available.objectIds !== undefined && available.objectIds?.indexOf(option.objectId) !== -1) !== undefined
							founded = isAvailable
							//we found a options so we can break out the object loop
							if (founded) {
								break
							}
						}
					}

					//one group was not abvailable so we dont need search more inside the maps
					if (founded == false) {
						selectMap = false
						break
					} else {
						selectMap = true
					}
				}
				if (selectMap) {
					//we have a valid map we can break
					map = m
					break
				}
			}

			setMap(map || maps[0])

			forceRender({})
		}
	}, [maps, hotspots, objects, selectedObjectGroups, availableLoungers, map])

	useEffect(() => {
		if (parentRef.current && map) {
			svg.current = parentRef.current.firstElementChild
			svg.current && svg.current.addEventListener("wheel", handleWheel, { passive: false })
			svg.current && svg.current.addEventListener("pointerdown", handleMapPointerDown)
			svg.current && svg.current.addEventListener("pointerup", handleMapPointerUp)
			svg.current && svg.current.addEventListener("pointerleave", handleMapPointerUp)

			const viewBox = svg.current.viewBox.baseVal
			initialViewBox.current = { x: viewBox.x, y: viewBox.y, width: viewBox.width, height: viewBox.height }

			forceRender({})
		}

		return () => {
			svg.current && svg.current.removeEventListener("wheel", handleWheel)
			svg.current && svg.current.removeEventListener("pointermove", handleMapPointerMove)
			svg.current && svg.current.removeEventListener("pointerdown", handleMapPointerDown)
			svg.current && svg.current.removeEventListener("pointerup", handleMapPointerUp)
			svg.current && svg.current.removeEventListener("pointerleave", handleMapPointerUp)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [parentRef.current, map])

	const hotspotsSorted = useMemo(() => {
		if (!map) return

		return hotspots.filter(e => e.mapId === map.mapId).sort((a, b) => a.y - b.y || a.x - b.x)
	}, [hotspots, map])

	const loungersSorted = useMemo(() => {
		if (!map || !objects?.length) return

		// Flatten all loungers from all groups and sort globally
		const allLoungers = objects.flatMap(group => (group.objects || []).filter(object => object.mapId === map.mapId))

		const validLoungerGraphics = allLoungers.filter(lounger => lounger.hotspotId && lounger.x && lounger.y)
		const sortedHotspotIds = hotspotsSorted.map(x => x.hotspotId)

		const idOrderMap = Object.fromEntries(sortedHotspotIds.map((id, index) => [id, index]))

		const sortedLoungers = validLoungerGraphics.sort((a, b) => {
			// First, sort by hotspot order
			const hotspotComparison = idOrderMap[a.hotspotId] - idOrderMap[b.hotspotId]
			if (hotspotComparison !== 0) return hotspotComparison

			// If same hotspot, sort by height
			return a.y - b.y
		})

		return sortedLoungers
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [objects, map])

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const raiseZoom = useCallback((x, y, w, h) => {
		svg.current.setAttributeNS(null, "viewBox", `${x} ${y} ${w} ${h}`)

		//in firefox the getCTM could be null, so we then jump to the screen ctm
		zoomLevel.current = (svg.current.getCTM() || svg.current.getScreenCTM()).a

		setShowHotspots(!(zoomLevel.current > 0.35))
	})

	const animateZoomToTarget = useCallback(
		(start, distance, duration = 400) => {
			let startTime = null

			function animate(timestamp) {
				if (!startTime) startTime = timestamp
				const runtime = timestamp - startTime
				const progress = Math.min(runtime / duration, 1)

				const x = start.x + progress * distance.x
				const y = start.y + progress * distance.y
				const w = start.w - progress * distance.w
				const h = start.h - progress * distance.h

				raiseZoom(x, y, w, h)

				if (runtime < duration) requestAnimationFrame(animate)
			}

			requestAnimationFrame(animate)
		},
		[raiseZoom],
	)

	const next = useCallback(async () => {
		if (usedCapacity < minimumCapacity && usedCapacity > 0) {
			raiseError(new Error(OrderErrors.allAdultNeedsAnLounger))
			return
		}

		if (countOfLoungerWithSelection > 0 && countOfLoungerWithSelection < loungers.length) {
			raiseError(new Error(OrderErrors.mapAllLoungerNeedsSelection))
			return
		}

		try {
			let rsp = await saveObjectGroupOptions({ loungers: loungers.filter(e => !e.selectedId), dateString: selectedDay.date, selectedTags }).unwrap()
			if (loungers.filter(e => e.selectedId).length) {
				let rsp2 = await saveObjectsOptions({ objectIds: selectedObjectIds, dateString: selectedDay.date }).unwrap()

				tracking.beds(false)
				setObjectGroupOptions([...rsp, ...rsp2])
				push("/order/bluphoria-selection", OrderAnimationTypes.fullSlide)
			} else {
				setObjectGroupOptions([...rsp])
				tracking.beds(true)
				push("/order/bluphoria-selection", OrderAnimationTypes.fullSlide)
			}
		} catch (e) {
			appStore.set(state => {
				return {
					order: {
						...state.order,
						loungers: [],
						objectGroupOptions: [],
					},
				}
			})
			pop(-1, OrderAnimationTypes.fullSlide)
			raiseError(e, undefined, false, true)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loungers, countOfLoungerWithSelection, selectedTags])

	const zoomTo = useCallback(
		(event, coordinates) => {
			let { x, y } = coordinates
			let margin = 600

			let box = event.target.getBBox()
			x = x || box.x
			y = y || box.y
			let width = box.width
			let height = box.height
			;+(margin * 1 || 0)

			if (margin) {
				x = x - margin / 2
				y = y - margin / 2
				width = width + margin
				height = height + margin
			}

			// From here on we use the x and y to animate...
			let vb = svg.current.viewBox.baseVal

			let distance = {
				x: x - vb.x,
				y: y - vb.y,
				w: vb.width - width,
				h: vb.height - height,
			}

			let start = {
				x: vb.x,
				y: vb.y,
				w: vb.width,
				h: vb.height,
			}

			animateZoomToTarget(start, distance)
		},
		[animateZoomToTarget],
	)

	const zoom = useCallback((direction, clientX, clientY, customFactor = 1.1) => {
		let factor = customFactor
		let width, height, x, y, deltaX, deltaY
		let pt = svg.current.createSVGPoint()
		pt.x = clientX
		pt.y = clientY
		let pos = pt.matrixTransform(svg.current.getScreenCTM().inverse())

		if (direction > 0) {
			// Zoom out
			if (zoomLevel.current < 0.1) return
			width = svg.current.viewBox.baseVal.width * factor
			height = svg.current.viewBox.baseVal.height * factor
			deltaX = pos.x - svg.current.viewBox.baseVal.x
			deltaY = pos.y - svg.current.viewBox.baseVal.y
			deltaX = deltaX * factor
			deltaY = deltaY * factor
			x = pos.x - deltaX
			y = pos.y - deltaY
		} else {
			// Zoom in
			if (zoomLevel.current > 1.5) return
			width = svg.current.viewBox.baseVal.width / factor
			height = svg.current.viewBox.baseVal.height / factor
			deltaX = pos.x - svg.current.viewBox.baseVal.x
			deltaY = pos.y - svg.current.viewBox.baseVal.y
			deltaX = deltaX / factor
			deltaY = deltaY / factor
			x = pos.x - deltaX
			y = pos.y - deltaY
		}

		raiseZoom(x, y, width, height)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleWheel = useCallback(event => {
		event.preventDefault()
		let { deltaY, clientX, clientY } = event
		zoom(deltaY, clientX, clientY)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleMapPointerDown = useCallback(event => {
		pointerStart.current = {}
		pointerStart.current.x = event.clientX
		pointerStart.current.y = event.clientY
		pointerStart.current.posX = svg.current.viewBox.baseVal.x
		pointerStart.current.posY = svg.current.viewBox.baseVal.y
		pointerStart.current.width = svg.current.viewBox.baseVal.width
		pointerStart.current.height = svg.current.viewBox.baseVal.height

		eventCache.current.push(event)

		svg.current.addEventListener("pointermove", handleMapPointerMove)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleMapPointerMove = useCallback(event => {
		event.preventDefault()
		event.stopImmediatePropagation()

		if (eventCache.current.length < 2) {
			let el = svg.current
			let start = pointerStart.current

			let distanceX = event.clientX - start.x
			let distanceY = event.clientY - start.y

			let matrix = el.getCTM() || el.getScreenCTM()
			let x = distanceX / matrix.a - start.posX
			let y = distanceY / matrix.d - start.posY

			el.setAttributeNS(null, "viewBox", `${-x} ${-y} ${start.width} ${start.height}`)
		} else {
			const index = eventCache.current.findIndex(cachedEv => cachedEv.pointerId === event.pointerId)
			eventCache.current[index] = event

			if (eventCache.current.length === 2) {
				// Calculate the distance between the two pointers
				const curDiffX = Math.abs(eventCache.current[0].clientX - eventCache.current[1].clientX)
				const curDiffY = Math.abs(eventCache.current[0].clientY - eventCache.current[1].clientY)
				const curDiff = Math.max(curDiffX, curDiffY)
				let cX = (eventCache.current[0].clientX + eventCache.current[1].clientX) / 2
				let cY = (eventCache.current[0].clientY + eventCache.current[1].clientY) / 2

				if (prevPinchDiff.current > 0) {
					if (curDiff > prevPinchDiff.current) {
						zoom(-1, cX, cY, 1.02)
					}
					if (curDiff < prevPinchDiff.current) {
						zoom(1, cX, cY, 1.02)
					}
				}

				// Cache the distance for the next move event
				prevPinchDiff.current = curDiff
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleMapPointerUp = useCallback(event => {
		event.stopImmediatePropagation()
		svg.current.removeEventListener("pointermove", handleMapPointerMove)
		pointerStart.current = undefined

		const index = eventCache.current.findIndex(cachedEv => cachedEv.pointerId === event.pointerId)
		eventCache.current.splice(index, 1)
		if (eventCache.current.length < 2) {
			prevPinchDiff.current = -1
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const zoomFromCenter = useCallback(
		direction => {
			if (svg.current) {
				const rect = svg.current.getBoundingClientRect()
				const centerX = rect.left + rect.width / 2
				const centerY = rect.top + rect.height / 2
				zoom(direction, centerX, centerY)
			}
		},
		[zoom],
	)

	// Pan the map by a certain amount with animation
	const panMap = useCallback((deltaX, deltaY) => {
		if (svg.current) {
			const viewBox = svg.current.viewBox.baseVal
			const panAmount = 100 // Pixels to pan per key press

			// Calculate the pan amount relative to the current zoom level
			const matrix = svg.current.getCTM() || svg.current.getScreenCTM()
			const scaledPanX = (deltaX * panAmount) / matrix.a
			const scaledPanY = (deltaY * panAmount) / matrix.d

			const targetX = viewBox.x + scaledPanX
			const targetY = viewBox.y + scaledPanY

			// Animate the pan without changing zoom level or hotspot visibility
			let startTime = null
			const duration = 300
			const startX = viewBox.x
			const startY = viewBox.y
			const distanceX = targetX - startX
			const distanceY = targetY - startY

			function animate(timestamp) {
				if (!startTime) startTime = timestamp
				const runtime = timestamp - startTime
				const progress = Math.min(runtime / duration, 1)

				const x = startX + progress * distanceX
				const y = startY + progress * distanceY

				svg.current.setAttributeNS(null, "viewBox", `${x} ${y} ${viewBox.width} ${viewBox.height}`)

				if (runtime < duration) requestAnimationFrame(animate)
			}

			requestAnimationFrame(animate)
		}
	}, [])

	// Check if an element is visible within the current viewBox
	const isElementInViewBox = useCallback(element => {
		if (!svg.current || !element) return true

		const viewBox = svg.current.viewBox.baseVal
		const bbox = element.getBBox()

		// Get the element's position in SVG coordinates
		const transform = element.transform.baseVal.consolidate()
		const matrix = transform ? transform.matrix : { e: 0, f: 0 }

		const elementX = bbox.x + matrix.e
		const elementY = bbox.y + matrix.f
		const elementWidth = bbox.width
		const elementHeight = bbox.height

		// Check if element is within viewBox bounds
		const isVisible =
			elementX >= viewBox.x &&
			elementY >= viewBox.y &&
			elementX + elementWidth <= viewBox.x + viewBox.width &&
			elementY + elementHeight <= viewBox.y + viewBox.height

		return isVisible
	}, [])

	// Shift viewBox to show the element (minimal shift, not centering)
	const shiftViewBoxToElement = useCallback(
		element => {
			if (!svg.current || !element) return

			const viewBox = svg.current.viewBox.baseVal
			const bbox = element.getBBox()

			// Get element position
			const transform = element.transform.baseVal.consolidate()
			const matrix = transform ? transform.matrix : { e: 0, f: 0 }

			const elementX = bbox.x + matrix.e
			const elementY = bbox.y + matrix.f
			const elementWidth = bbox.width
			const elementHeight = bbox.height

			// Add padding so element isn't right at the edge
			const padding = 50

			// Calculate minimum shift needed to bring element into view
			let shiftX = 0
			let shiftY = 0

			// Check if element is outside on the left
			if (elementX < viewBox.x) {
				shiftX = elementX - viewBox.x - padding
			}
			// Check if element is outside on the right
			else if (elementX + elementWidth > viewBox.x + viewBox.width) {
				shiftX = elementX + elementWidth - (viewBox.x + viewBox.width) + padding
			}

			// Check if element is outside on the top
			if (elementY < viewBox.y) {
				shiftY = elementY - viewBox.y - padding
			}
			// Check if element is outside on the bottom
			else if (elementY + elementHeight > viewBox.y + viewBox.height) {
				shiftY = elementY + elementHeight - (viewBox.y + viewBox.height) + padding
			}

			// Only animate if we need to shift
			if (shiftX !== 0 || shiftY !== 0) {
				const newX = viewBox.x + shiftX
				const newY = viewBox.y + shiftY

				// Animate to new position
				const start = {
					x: viewBox.x,
					y: viewBox.y,
					w: viewBox.width,
					h: viewBox.height,
				}

				const distance = {
					x: newX - viewBox.x,
					y: newY - viewBox.y,
					w: 0, // Keep same zoom level
					h: 0,
				}

				animateZoomToTarget(start, distance, 300)
			}
		},
		[animateZoomToTarget],
	)

	const sortLoungersByTabIndex = useCallback(loungers => {
		return loungers.sort((a, b) => a.element.getAttribute("tabindex") - b.element.getAttribute("tabindex"))
	}, [])

	const getAvailableLoungers = useCallback(() => {
		return allLoungersRef.current.filter(loungerData => {
			const isAvailable = availableLoungers.find(available => available.objectIds?.indexOf(loungerData.objectId) !== -1)
			return isAvailable && loungerData.element?.getAttribute("tabindex") !== "-1"
		})
	}, [availableLoungers])

	const handleHotspotClick = useCallback(
		(event, hotspot) => {
			setShowHotspots(false)
			zoomTo(event, hotspot)

			// After zooming, focus the first available lounger in this hotspot
			setTimeout(() => {
				const thisHotspotLoungers = allLoungersRef.current.filter(loungerData => loungerData.hotspotId === hotspot.hotspotId)
				const loungersInHotspot = sortLoungersByTabIndex(thisHotspotLoungers)

				// Find first available lounger
				for (const loungerData of loungersInHotspot) {
					const isAvailable = availableLoungers.find(available => available.objectIds?.indexOf(loungerData.objectId) !== -1)
					if (isAvailable && loungerData.element.getAttribute("tabindex") !== "-1") {
						loungerData.element.focus()
						break
					}
				}
			}, 500)
		},
		[zoomTo, availableLoungers, sortLoungersByTabIndex],
	)

	// Focus trap for loungers - cycle through available loungers/hotspots when tabbing
	useEffect(() => {
		const handleLoungerTab = e => {
			if (e.key !== KEYBOARD_KEY_SET.Tab) return
			const currentElement = e.target

			if (showHotspots) {
				const focusedHotspotId = parseInt(currentElement.getAttribute("data-hotspot-id"), 10)
				if (!focusedHotspotId || hotspotsSorted.length === 0) return

				/**
				 * If Tab was pressed and last hotspot is focused -> focus first hotspot,
				 * Else if Shift + Tab was pressed & first element focused -> focus previous hotspot element
				 */
				if (!e.shiftKey) {
					if (focusedHotspotId === hotspotsSorted[hotspotsSorted?.length - 1].hotspotId) {
						const firstHotspot = hotspotsSorted[0]
						const firstHotspotElement = document.querySelector(`[data-hotspot-id="${firstHotspot.hotspotId}"]`)

						if (firstHotspotElement) {
							e.preventDefault()
							firstHotspotElement.focus()
							return
						}
					}
				} else {
					if (focusedHotspotId === hotspotsSorted[0].hotspotId) {
						e.preventDefault()
						const lastHotspot = hotspotsSorted[hotspotsSorted?.length - 1]
						const lastHotspotElement = document.querySelector(`[data-hotspot-id="${lastHotspot.hotspotId}"]`)

						lastHotspotElement.focus()
						return
					}
				}
			}

			const availableLoungers = sortLoungersByTabIndex(getAvailableLoungers())
			if (availableLoungers.length === 0) return

			const currentIndex = availableLoungers.findIndex(l => l.element === currentElement)

			if (currentIndex === -1) return

			if (e.shiftKey) {
				// Shift+Tab on first lounger -> focus last lounger
				if (currentIndex === 0) {
					e.preventDefault()
					availableLoungers[availableLoungers.length - 1].element.focus()
				}
			} else {
				// Tab on last lounger -> go to first
				if (currentIndex === availableLoungers.length - 1) {
					e.preventDefault()
					availableLoungers[0].element.focus()
				}
			}
		}

		if (isFocusInsideTheMap && svg.current) {
			svg.current.addEventListener("keydown", handleLoungerTab)
			return () => {
				svg.current?.removeEventListener("keydown", handleLoungerTab)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFocusInsideTheMap, sortLoungersByTabIndex, getAvailableLoungers, showHotspots])

	useEffect(() => {
		if (!isFocusInsideTheMap) return

		const { hotspotId, loungerObjectId } = lastFocusedMapElement.current || {}

		// If showing hotspots, focus the first hotspot
		if (showHotspots) {
			if (hotspotId) {
				const previouslyFocusedHotspot = document.querySelector(`[data-hotspot-id="${hotspotId}"]`)

				if (previouslyFocusedHotspot) {
					previouslyFocusedHotspot.focus()
				}
				return
			}

			const firstHotspot = hotspotsSorted[0]

			if (firstHotspot) {
				const hotspotElement = document.querySelector(`[data-hotspot-id="${firstHotspot.hotspotId}"]`)
				hotspotElement && hotspotElement.focus()
			}
		} else {
			if (loungerObjectId) {
				const previouslyFocusedLounger = allLoungersRef.current.find(x => x?.objectId === loungerObjectId)

				if (previouslyFocusedLounger?.element) {
					previouslyFocusedLounger?.element?.focus()
					return
				}
			}

			// Focus the first available lounger of that hotspot
			const sortedLoungers = sortLoungersByTabIndex(allLoungersRef.current)

			// Find first available lounger
			for (const loungerData of sortedLoungers) {
				const isAvailable = availableLoungers.find(available => available.objectIds?.indexOf(loungerData.objectId) !== -1)
				if (isAvailable && loungerData.element?.getAttribute("tabindex") !== "-1") {
					loungerData.element.focus()
					break
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFocusInsideTheMap, sortLoungersByTabIndex, availableLoungers, hotspotsSorted])

	/*
	 * We handle keyDown actions in three different places, for three different purposes:
	 * 1. divMap element keydown listener handles main navigation actions.
	 * Listens for Zoom in/out, unfocus map, move to controls etc.
	 * 2. Lounger item key listener handles the selection/unselection/focus
	 * of each element and the
	 * 3. svg.current keydown listener, handles the "focus trap" => locking
	 * the focusing order of the elements inside the map, not allowing to move in/out to controls etc.
	 */
	const handleKeyPressCallback = useCallback(
		e => {
			return handleKeyPress(e, {
				[KEYBOARD_KEY_SET.Select]: () => {
					e.preventDefault()
					setIsFocusInsideTheMap(true)
				},
				[KEYBOARD_KEY_SET.Escape]: () => {
					setIsFocusInsideTheMap(false)

					const activeElement = document.activeElement
					const loungerObjectId = activeElement?.getAttribute("data-lounger-object-id")
					const hotspotId = activeElement?.getAttribute("data-hotspot-id")

					lastFocusedMapElement.current = {
						hotspotId: hotspotId && parseInt(hotspotId),
						loungerObjectId: loungerObjectId && parseInt(loungerObjectId, 10),
					}

					const divMap = document.getElementById("divMap")
					divMap?.focus()
				},
				[KEYBOARD_KEY_SET.Plus]: () => {
					e.preventDefault()
					zoomFromCenter(-1)
				},
				[KEYBOARD_KEY_SET.Minus]: () => {
					e.preventDefault()
					zoomFromCenter(1)
				},
				[KEYBOARD_KEY_SET.ArrowUp]: () => {
					e.preventDefault()
					panMap(0, -1)
				},
				[KEYBOARD_KEY_SET.ArrowDown]: () => {
					e.preventDefault()
					panMap(0, 1)
				},
				[KEYBOARD_KEY_SET.ArrowLeft]: () => {
					e.preventDefault()
					panMap(-1, 0)
				},
				[KEYBOARD_KEY_SET.ArrowRight]: () => {
					e.preventDefault()
					panMap(1, 0)
				},
				[KEYBOARD_KEY_SET.Tab]: () => {
					if (isFocusInsideTheMap) {
						return
					}
					e.preventDefault()

					const zoomInButton = document.querySelector(".zoom-controls .zoom-btn")
					if (zoomInButton) {
						zoomInButton.focus()
					}
				},
			})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isFocusInsideTheMap],
	)

	return (
		<OrderSubPage>
			<ContentHeader canGoBack colorStyle="white" animation={OrderAnimationTypes.fullSlide}>
				<span className="semibold">
					{loungerMapSelectionTitleSingle
						? loungerMapSelectionTitleSingle.replace("${selected}", countOfLoungerWithSelection).replace("${maximum}", loungers.length)
						: ""}
				</span>
			</ContentHeader>
			<OrderContentBody className="h-100 flex flex-column relative of-hidden">
				<div role="group" aria-label={mapControlsLabel}>
					<div className="flex mt-2">
						{maps &&
							maps.map &&
							maps.map(smap => {
								return (
									<button
										className={
											"map-btn px-3 py-2 border color-border-primary mr-2 rounded-top-3 clickable f6" +
											classNamez({
												"color-bg-primary": smap.mapId === (map ? map.mapId : undefined),
												"color-fg-white": smap.mapId === (map ? map.mapId : undefined),
											})
										}
										onClick={() => {
											setMap(smap)
											setShowHotspots(true)
										}}
										key={smap.mapId}
									>
										{smap.translation || smap.map}
									</button>
								)
							})}
					</div>

					<div className="absolute flex p-3 flex-wrap">
						{tags &&
							tags.map((tag, index) => {
								return (
									<button
										key={index}
										className={
											"map-btn px-3 py-2 border color-border-primary mr-2 rounded-3 clickable f6 mb-2" +
											classNamez({
												"color-bg-default": selectedTags.indexOf(tag.tagId) === -1,
												"color-bg-primary": selectedTags.indexOf(tag.tagId) !== -1,
												"color-fg-white": selectedTags.indexOf(tag.tagId) !== -1,
											})
										}
										onClick={() => {
											let t = [...selectedTags]
											if (t.indexOf(tag.tagId) === -1) {
												t.push(tag.tagId)
											} else {
												t.splice(t.indexOf(tag.tagId), 1)
											}
											setSelectedTags(t)
										}}
										// When items inside the map receive focus, the elements' aria get announced too. Hide until outside the map
										aria-hidden={isFocusInsideTheMap}
									>
										{tag.tag}
									</button>
								)
							})}
					</div>
				</div>
				<div
					ref={parentHandler}
					className="h-100 flex of-hidden"
					id="divMap"
					tabIndex={0}
					onKeyDown={handleKeyPressCallback}
					role="application"
					aria-label={!isFocusInsideTheMap ? mapSvgLabel : showHotspots ? mapSvgOnSelectLabel : ""}
				/>

				{svg.current && hotspotsSorted && map && (
					<>
						{hotspotsSorted.map(hotspot => {
							return (
								<Hotspot map={svg.current} hotspot={hotspot} key={hotspot.hotspotId} show={showHotspots} onClick={handleHotspotClick} tags={selectedTags} />
							)
						})}
					</>
				)}

				{svg.current && decorations && map && (
					<>
						{decorations
							.filter(e => e.mapId === map.mapId)
							.map(decoration => {
								return <Decoration map={svg.current} decoration={decoration} key={decoration.decorationId} show={showHotspots} />
							})}
					</>
				)}

				{svg.current && objects && map && (
					<>
						{(() => {
							/*
							 * Create a key-value pair where the key is the objectid
							 * and value in the tabIndex of the already sorted loungers.
							 * This way, we will know when it each lounger needs to be focused based
							 * on its tabIndex
							 */
							const tabIndexMap = {}
							loungersSorted.forEach((lounger, index) => {
								/**
								 * Add +1 to avoid focus jumping between the map and the controls on top of the map
								 * since all interactive elements have by default tabIndex={0}
								 */
								tabIndexMap[lounger.objectId] = index + 1
							})

							return objects.map(group => {
								return (
									<LoungerGroup
										map={svg.current}
										group={group}
										key={group.objectGroupId}
										show={!showHotspots}
										selectable={selectedObjectGroups.indexOf(group.objectGroupId) !== -1}
										mapId={map.mapId}
										tags={selectedTags}
										hotspots={hotspots}
										tabIndexMap={tabIndexMap}
										allLoungersRef={allLoungersRef}
										onCheckVisibility={isElementInViewBox}
										onShiftToElement={shiftViewBoxToElement}
										onLoungerSelect={(group, loungerId, objectId) => {
											if (eventCache.current.length > 1) {
												return
											}
											const added = selectLoungerId(group, objectId, __types)
											let price

											if (added) {
												let types = []
												Object.keys(__types).forEach(e => {
													types.push(...__types[e])
												})
												let cmsGroup = types.find(e => e.objectGroupId === group.objectGroupId)
												let availability = availableLoungers.find(e => e.objectGroupId == cmsGroup.objectGroupId)
												price = availability.price
												setAddedGroup({ ...cmsGroup, loungerId, ...group, price: availability.price })
											}

											setCurrentLoungerGroupInteraction({ group, loungerId: objectId, added, price })
										}}
									/>
								)
							})
						})()}
					</>
				)}

				{/* Screen reader announcement for lounger selection */}
				<div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
					{currentLoungerGroupInteraction
						? `${currentLoungerGroupInteraction.group?.objectGroup}  ${currentLoungerGroupInteraction.group?.abbreviationZone}-${currentLoungerGroupInteraction.group?.abbreviation}  ${currentLoungerGroupInteraction.loungerId}
                        ${currentLoungerGroupInteraction.added ? addedToCartLabel : removedFromCartLabel}
                        ${currentLoungerGroupInteraction.price ? `, ${forString} ${getPriceString(currentLoungerGroupInteraction.price)}` : ""}`
						: ""}
				</div>

				<div role="group" className="absolute zoom-controls px-3 px-lg-8 pb-4 flex flex-column" aria-label={zoomControlsLabel}>
					<button
						className="zoom-btn color-bg-default color-fg-primary"
						onClick={() => zoomFromCenter(-1)}
						onKeyDown={e => {
							handleKeyPress(e, {
								[KEYBOARD_KEY_SET.Select]: () => {
									e.preventDefault()
									zoomFromCenter(-1)
								},
								[KEYBOARD_KEY_SET.ShiftAndTab]: () => {
									e.preventDefault()
									const divMap = document.getElementById("divMap")
									divMap?.focus()
								},
							})
						}}
						aria-label={buttonLabelZoomIn}
						aria-hidden={isFocusInsideTheMap}
						tabIndex={0}
						onFocus={() => {
							setIsFocusInsideTheMap(false)
						}}
					>
						<FontAwesomeIcon icon={faPlus} />
					</button>
					<button
						className="zoom-btn color-bg-default color-fg-primary"
						onClick={() => zoomFromCenter(1)}
						onKeyDown={e => {
							handleKeyboardSelect(e, () => zoomFromCenter(1))
						}}
						aria-label={buttonLabelZoomOut}
						tabIndex={0}
					>
						<FontAwesomeIcon icon={faMinus} />
					</button>
				</div>

				<AddedLoungerMessage group={addedGroup} />
			</OrderContentBody>
			<ContentFooter
				process={3}
				price={price}
				priceAbsolute
				buttonProps={{
					text: countOfLoungerWithSelection ? buttonNext : buttonAutomaticAssign,
					isLoading: false,
					onNext: next,
					disabled: isHandicapped && countOfLoungerWithSelection < 1,
				}}
			/>
		</OrderSubPage>
	)
}

export const AddedLoungerMessage = ({ group }) => {
	const {
		data: { for: forString },
	} = useGetStrings(undefined, { defaultData: {} })
	const [show, setShow] = useState(false)
	const timer = useRef(null)
	useEffect(() => {
		if (group) {
			setShow(true)
			clearTimeout(timer.current)
			timer.current = setTimeout(() => {
				setShow(false)
			}, 5000)

			return () => {
				setShow(false)
				clearTimeout(timer.current)
			}
		}
	}, [group, setShow])

	if (show && group) {
		return (
			<div className="absolute color-bg-default boxShadow messageAnimation flex">
				<div
					style={{ background: "url(" + group.image + ")", height: "80px", width: "80px", backgroundPosition: "cover", backgroundSize: "cover" }}
					className=""
				/>
				<div className="py-3 px-3">
					<div className="flex flex-items-center">
						{Array.apply(null, Array(group.capacity)).map((e, i) => {
							return (
								<div className="p-1 color-bg-primary color-fg-on-primary f6 flex-shrink-0" key={i}>
									<FontAwesomeIcon icon={faUser} />
								</div>
							)
						})}
						<div className="ml-1" style={{ whiteSpace: "nowrap" }}>
							{group.loungerType}{" "}
							<span className="color-fg-secondary">
								({group.abbreviationZone}-{group.abbreviation}
								{group.loungerId})
							</span>
						</div>
					</div>
					<div className="color-fg-secondary mt-1">
						{forString} {getPriceString(group.price)}
					</div>
				</div>
				<div className="flex flex-center f0 color-fg-green-700 pr-3">
					<FontAwesomeIcon icon={faCheck} />
				</div>
			</div>
		)
	}
}
